import iniciativaVideoImg from 'assets/img/2026/iniciativa_video.png';
import logoPodio from 'assets/img/2026/logo_podio.png';
import { useAuth } from 'context/AuthContext';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { BsEmojiSmile } from 'react-icons/bs';
import { FaCheck, FaTrophy, FaUser } from 'react-icons/fa';
import { IoChevronDown, IoCloudDownloadOutline } from 'react-icons/io5';
import { MdBookmark, MdBookmarkBorder } from 'react-icons/md';
import {
  registrarElogio,
  registrarFavorito
} from 'services/iniciativa-action.service';
import { IniciativaFormatada } from 'types/iniciativa';
import { clearRegistrationUser } from 'utils/fns';
import { participantesVisiveis } from 'utils/participantes-validacao';
import { getCategoriaCorHex, getClassificacaoLabel, getResultadoLabel, isCategoriaElbb } from 'utils/iniciativa-categoria';
import './ModalIniciativa.scss';

interface ModalIniciativaProps {
  isOpen: boolean;
  onClose: () => void;
  iniciativa: IniciativaFormatada;
  /** Quando informada, renderiza o header com gradiente ouro / prata / bronze
   *  no lugar da moldura padrão de "Classificada Etapa X". */
  podioPosition?: 1 | 2 | 3;
  /** Quando true (premiação do pódio na home), oculta participantes não-válidos
   *  (não confirmados, impedidos e sem pré-requisito), mostrando só os "Aptos". */
  ocultarNaoValidos?: boolean;
}

export const ModalIniciativa: React.FC<ModalIniciativaProps> = ({
  isOpen,
  onClose,
  iniciativa,
  podioPosition,
  ocultarNaoValidos,
}) => {
  const { user } = useAuth();
  const [isComplimentOpen, setIsComplimentOpen] = useState(false);
  const [selectedCompliment, setSelectedCompliment] = useState<string>(iniciativa.usuarioElogio || '');
  const [isFavorited, setIsFavorited] = useState(iniciativa.usuarioFavoritou);
  const [loadingAction, setLoadingAction] = useState(false);
  const complimentRef = useRef<HTMLDivElement>(null);
  const corCategoriaHex = getCategoriaCorHex(iniciativa.categoryPremio);
  const { lista: participantesVis, numerador: participantesNumerador, denominador: participantesDenominador } =
    participantesVisiveis(iniciativa.participantes, {
      ativo: !!ocultarNaoValidos,
      validadeApurada: iniciativa.validadeApurada,
      totalValidos: iniciativa.totalValidos,
      totalOriginal: iniciativa.totalOriginal,
    });
  const modalRef = useRef<HTMLDivElement>(null);
  const isElbb = isCategoriaElbb(iniciativa.categoryPremio);
  const indicadosSet = new Set(
    isElbb
      ? iniciativa.participantes.map((p) => clearRegistrationUser(p.matricula))
      : (iniciativa.iniciativaOriginal?.indicados ?? []).map((chave) =>
          clearRegistrationUser(chave)
        )
  );
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const iniciativaAny = iniciativa as any;
  const documentacaoUrl =
    iniciativaAny?.documentacao ||
    iniciativaAny?.iniciativaOriginal?.documentacao ||
    iniciativaAny?.iniciativaOriginal?.documentacaoComplementar ||
    iniciativaAny?.iniciativaOriginal?.documento?.find((doc: any) => doc?.type === 'file' && doc?.file)?.file;
  const descricaoExibida =
    iniciativaAny?.iniciativaOriginal?.description ||
    iniciativa.descricao ||
    '';
  const resultadoOriginalApi = iniciativaAny?.iniciativaOriginal?.result;
  const resultadoExibido =
    resultadoOriginalApi ||
    iniciativa.texto ||
    '';
  const labelResultado = getResultadoLabel(
    iniciativa.categoryPremio,
    iniciativaAny?.iniciativaOriginal?.indicator,
    resultadoOriginalApi || iniciativa.texto
  );

  const complimentOptions = ['Arrasou!', 'Iniciativa inspiradora!', 'Superou expectativas!'];

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (complimentRef.current && !complimentRef.current.contains(event.target as Node)) {
        setIsComplimentOpen(false);
      }
    };

    if (isComplimentOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isComplimentOpen]);

  // Semântica de diálogo acessível: ao abrir, leva o foco para o modal e
  // guarda o elemento previamente focado; ao fechar, devolve o foco a ele.
  // Escape fecha o modal e Tab fica preso dentro dele (focus trap), como manda
  // o padrão WAI-ARIA de dialog — essencial para quem navega por teclado/leitor.
  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const modalNode = modalRef.current;
    modalNode?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !modalNode) return;

      const focusables = modalNode.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  const handleComplimentSelect = async (compliment: string) => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      setSelectedCompliment(compliment);
      setIsComplimentOpen(false);

      await registrarElogio(user._id, {
        id_iniciativa: iniciativa.id,
        elogio: compliment
      });
    } catch (error) {
      setSelectedCompliment('');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      const novoStatus = !isFavorited;

      await registrarFavorito(user._id, {
        id_iniciativa: iniciativa.id,
        ativo: novoStatus
      });

      setIsFavorited(novoStatus);
    } catch (error) {
      // Erro silencioso
    } finally {
      setLoadingAction(false);
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDownload = () => {
    if (!documentacaoUrl) return;

    window.open(documentacaoUrl, '_blank', 'noopener,noreferrer');
  };

  // Renderizado via portal em `document.body` para escapar de qualquer
  // ancestral com `transform`/`filter`/`perspective` (que capturariam o
  // `position: fixed` do overlay e prenderiam o modal dentro do card).
  return ReactDOM.createPortal(
    <div className="modal-iniciativa-overlay" onClick={handleOverlayClick}>
      <div
        className={[
          'modal-iniciativa',
          iniciativa.classificadaEtapa1 || iniciativa.classificadaEtapa2 || podioPosition
            ? 'modal-iniciativa--classificada'
            : '',
          iniciativa.classificadaEtapa2 && !podioPosition ? 'modal-iniciativa--classificada-etapa2' : '',
          podioPosition ? `modal-iniciativa--podio-${podioPosition}` : '',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-iniciativa-title"
        tabIndex={-1}
      >
        {(iniciativa.classificadaEtapa1 || iniciativa.classificadaEtapa2 || podioPosition) && (
          <div className="modal-iniciativa__classificada-header">
            <span className="modal-iniciativa__classificada-label" tabIndex={0}>
              {podioPosition ? (
                <img
                  src={logoPodio}
                  alt=""
                  aria-hidden="true"
                  className="modal-iniciativa__podio-logo"
                />
              ) : (
                <FaTrophy size={24} aria-hidden="true" />
              )}
              {getClassificacaoLabel({
                classificadaEtapa1: iniciativa.classificadaEtapa1,
                classificadaEtapa2: iniciativa.classificadaEtapa2,
                position: podioPosition,
              }).classificacao}
            </span>
            <div
              className={`modal-iniciativa__badge tag-${iniciativa.corCategoria}`}
              style={{ backgroundColor: corCategoriaHex }}
              tabIndex={0}
              aria-label={`Categoria: ${iniciativa.categoria}`}
            >
              {iniciativa.categoria}
            </div>
          </div>
        )}
        <div className="modal-iniciativa__body">
          <div className="modal-iniciativa__content-wrapper">
            <div className="modal-iniciativa__left">
              {(iniciativa.videoUrl || iniciativa.imagem) && (
                iniciativa.videoUrl ? (
                  <a
                    href={iniciativa.originalVideoUrl || iniciativa.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'block', borderRadius: '12px', overflow: 'hidden' }}
                    aria-label={`Assistir ao vídeo da iniciativa ${iniciativa.titulo} (abre em nova aba)`}
                  >
                    <img
                      src={iniciativaVideoImg}
                      alt=""
                      aria-hidden="true"
                      style={{ width: '100%', display: 'block' }}
                    />
                  </a>
                ) : (
                  <div className="modal-iniciativa__video-container">
                    <img
                      src={iniciativa.imagem}
                      alt={`Imagem da iniciativa ${iniciativa.titulo}`}
                      className="modal-iniciativa__video-thumbnail"
                      tabIndex={0}
                    />
                  </div>
                )
              )}

              {documentacaoUrl && (
                <div className="modal-iniciativa__documentacao">
                  <p className="modal-iniciativa__documentacao-label" tabIndex={0}>
                    Documentação complementar da realização
                  </p>
                  <button
                    className="modal-iniciativa__download-btn"
                    onClick={handleDownload}
                    aria-label="Baixar documentação complementar da realização"
                  >
                    <IoCloudDownloadOutline size={20} aria-hidden="true" />
                    <span>DOWNLOAD</span>
                  </button>
                </div>
              )}

              {(iniciativa.elogiosPorTipo.length > 0 || iniciativa.totalFavoritos > 0) && (
                <div className="modal-iniciativa__feedback-box">
                  <h3 className="modal-iniciativa__feedback-title">Feedback na iniciativa:</h3>
                  <div className="modal-iniciativa__feedback-list">
                    {iniciativa.elogiosPorTipo.map((item, index) => (
                      <div key={index} className="modal-iniciativa__feedback-item" role="listitem" tabIndex={0} aria-label={`${item.quantidade} ${item.elogio}`}>
                        <span className="modal-iniciativa__feedback-count">{String(item.quantidade).padStart(2, '0')}</span>
                        <span className="modal-iniciativa__feedback-label">{item.elogio}</span>
                      </div>
                    ))}
                    {iniciativa.totalFavoritos > 0 && (
                      <div className="modal-iniciativa__feedback-item" role="listitem" tabIndex={0} aria-label={`${iniciativa.totalFavoritos} salvos nas favoritas`}>
                        <span className="modal-iniciativa__feedback-count">{String(iniciativa.totalFavoritos).padStart(2, '0')}</span>
                        <span className="modal-iniciativa__feedback-label">Salvos nas favoritas</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-iniciativa__right">
              {!iniciativa.classificadaEtapa1 && !iniciativa.classificadaEtapa2 && (
                <div className="modal-iniciativa__header">
                  <div
                    className={`modal-iniciativa__badge tag-${iniciativa.corCategoria}`}
                    style={{ backgroundColor: corCategoriaHex }}
                    tabIndex={0}
                    aria-label={`Categoria: ${iniciativa.categoria}`}
                  >
                    {iniciativa.categoria}
                  </div>
                </div>
              )}
              <h2 className="modal-iniciativa__title" id="modal-iniciativa-title" tabIndex={0}>{iniciativa.titulo}</h2>
              <div className="modal-iniciativa__description">
                <p className="modal-iniciativa__subtitle" tabIndex={0}>
                  {descricaoExibida}
                </p>

                <div className="modal-iniciativa__info-box">
                  <div className="modal-iniciativa__info-item" tabIndex={0} aria-label={`Cadastrado por: ${iniciativa.cadastradoDisplay || iniciativa.cadastradoPor}, em ${iniciativa.cadastradoData}`}>
                    <span className="modal-iniciativa__info-label">Cadastrado por:</span>
                    <span className="modal-iniciativa__info-value">
                      {iniciativa.cadastradoDisplay || iniciativa.cadastradoPor}, em {iniciativa.cadastradoData}
                    </span>
                  </div>
                  <div className="modal-iniciativa__info-item" tabIndex={0} aria-label={`Inscrito por: ${iniciativa.inscritoDisplay || iniciativa.inscritoPor}, em ${iniciativa.inscritoData}`}>
                    <span className="modal-iniciativa__info-label">Inscrito por:</span>
                    <span className="modal-iniciativa__info-value">
                      {iniciativa.inscritoDisplay || iniciativa.inscritoPor}, em {iniciativa.inscritoData}
                    </span>
                  </div>
                </div>

                {labelResultado.mostrar && !!resultadoExibido && (
                  <div className="modal-iniciativa__question">
                    <h3 className="modal-iniciativa__question-title" tabIndex={0}>
                      {labelResultado.titulo}
                    </h3>
                    <p className="modal-iniciativa__question-text" tabIndex={0}>
                      {resultadoExibido}
                    </p>
                  </div>
                )}

                <div className="modal-iniciativa__participantes" role="region" aria-label="Participantes da realização">
                  <h3 className="modal-iniciativa__participantes-title" tabIndex={0}>
                    Participantes da realização ({participantesNumerador} de {participantesDenominador} confirmados)
                  </h3>
                  <div className="modal-iniciativa__participantes-grid" role="list">
                    {participantesVis.map((participante, index) => {
                      const isIndicado = indicadosSet.has(
                        clearRegistrationUser(participante.matricula)
                      );
                      const aria = `${participante.displayName || participante.nome} — ${participante.confirmado || participante.isOwner ? 'confirmado' : 'pendente'}${isIndicado ? ', indicado' : ''}`;
                      return (
                        <div
                          key={index}
                          className={`modal-iniciativa__participante ${participante.confirmado || participante.isOwner ? 'confirmado' : 'pendente'
                            }`}
                          role="listitem"
                          tabIndex={0}
                          aria-label={aria}
                        >
                          <div className="modal-iniciativa__participante-content">
                            {isIndicado ? (
                               <FaUser className="modal-iniciativa__participante-avatar" size={18} aria-hidden="true" />
                              //se voltar indicados trocar pra esse icon
                              // <RiUserStarLine
                              //   className="modal-iniciativa__participante-avatar modal-iniciativa__participante-avatar--indicado"
                              //   size={18}
                              //   aria-label="Indicado"
                              // />
                            ) : (
                              <FaUser className="modal-iniciativa__participante-avatar" size={18} aria-hidden="true" />
                            )}

                            {(participante.confirmado || participante.isOwner) && (
                              <FaCheck className="modal-iniciativa__participante-check" size={18} aria-hidden="true" />
                            )}

                            <span className="modal-iniciativa__participante-nome">
                              {participante.displayName || `${participante.nome} (${participante.matricula})`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="modal-iniciativa__action-bar">
                  <div className="modal-iniciativa__action-left">
                    <div className="modal-iniciativa__icon-wrapper">
                      <BsEmojiSmile size={20} aria-hidden="true" style={{ color: selectedCompliment ? '#5D5FEF' : '#666666' }} />
                      {iniciativa.totalElogios > 0 && (
                        <span className="modal-iniciativa__badge-count" aria-label={`${iniciativa.totalElogios} elogios`}>{iniciativa.totalElogios}</span>
                      )}
                    </div>
                    <div className="modal-iniciativa__compliment-container" ref={complimentRef}>
                      <button
                        className="modal-iniciativa__compliment-dropdown"
                        onClick={() => !loadingAction && setIsComplimentOpen(!isComplimentOpen)}
                        aria-expanded={isComplimentOpen}
                        aria-haspopup="listbox"
                        aria-label={selectedCompliment ? `Elogio selecionado: ${selectedCompliment}` : 'Faça um elogio'}
                        disabled={loadingAction}
                        style={{
                          borderColor: selectedCompliment ? '#5D5FEF' : '#E0E7FF',
                          color: selectedCompliment ? '#5D5FEF' : '#666666'
                        }}
                      >
                        <span>{selectedCompliment || 'Faça um elogio!'}</span>
                        <IoChevronDown size={16} aria-hidden="true" />
                      </button>
                      {isComplimentOpen && !loadingAction && (
                        <div className="modal-iniciativa__compliment-menu" role="listbox" aria-label="Opções de elogio">
                          {complimentOptions.map((compliment) => (
                            <button
                              key={compliment}
                              className="modal-iniciativa__compliment-option"
                              role="option"
                              aria-selected={selectedCompliment === compliment}
                              onClick={() => handleComplimentSelect(compliment)}
                            >
                              {compliment}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-iniciativa__action-right">
                    <div className="modal-iniciativa__icon-wrapper">
                      <button
                        className="modal-iniciativa__action-icon-btn"
                        aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
                        aria-pressed={isFavorited}
                        onClick={handleToggleFavorite}
                        disabled={loadingAction}
                        style={{
                          opacity: loadingAction ? 0.6 : 1,
                          color: isFavorited ? '#5D5FEF' : '#666666'
                        }}
                      >
                        {isFavorited ? <MdBookmark size={20} aria-hidden="true" /> : <MdBookmarkBorder size={20} aria-hidden="true" />}
                      </button>
                      {iniciativa.totalFavoritos > 0 && (
                        <span className="modal-iniciativa__badge-count" aria-label={`${iniciativa.totalFavoritos} favoritos`}>{iniciativa.totalFavoritos}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-iniciativa__footer">
          <button
            className="modal-iniciativa__fechar-btn"
            onClick={onClose}
            aria-label="Fechar detalhes da iniciativa"
          >
            FECHAR
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
